import React, { useState } from 'react';
import { HiOutlineArrowDown, HiOutlineArrowUp, HiOutlineDuplicate, HiOutlineTrash } from 'react-icons/hi';
import { EmailBlock, EmailBlockKind, TemplateVariable } from '../../../../../../../types/communication';
import { EMAIL_BLOCK_LABELS, createBlock, newBlockId } from '../../../../../../../utils/emailHtml';
import AddBlockMenu from './AddBlockMenu';
import EmailBlockFields from './EmailBlockFields';
import { BLOCK_ICONS } from './blockIcons';

interface EmailBlockListProps {
    blocks: EmailBlock[];
    variables: TemplateVariable[];
    disabled: boolean;
    onChange: (blocks: EmailBlock[]) => void;
}

const plainText = (html: string): string => html.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

const summary = (block: EmailBlock): string => {
    if (block.kind === 'BUTTON') return `${block.text ?? ''} → ${block.url ?? ''}`;
    if (block.kind === 'TEXT') return plainText(block.text ?? '');
    return (block.text ?? '').replace(/\s+/g, ' ');
};

const IconButton: React.FC<{ label: string; onClick: () => void; disabled?: boolean; danger?: boolean; children: React.ReactNode }> = ({
    label,
    onClick,
    disabled,
    danger,
    children,
}) => (
    <button
        type="button"
        title={label}
        aria-label={label}
        disabled={disabled}
        onClick={(e) => {
            e.stopPropagation();
            onClick();
        }}
        className={`p-1 rounded-md text-gray-400 disabled:opacity-30 ${danger ? 'hover:text-red-600 hover:bg-red-50' : 'hover:text-gray-700 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
    >
        {children}
    </button>
);

const EmailBlockList: React.FC<EmailBlockListProps> = ({ blocks, variables, disabled, onChange }) => {
    const [selectedId, setSelectedId] = useState<string | null>(blocks[0]?.id ?? null);

    const insertAt = (index: number, kind: EmailBlockKind) => {
        const block = createBlock(kind);
        onChange([...blocks.slice(0, index), block, ...blocks.slice(index)]);
        setSelectedId(block.id);
    };

    const move = (index: number, delta: number) => {
        const next = [...blocks];
        const [item] = next.splice(index, 1);
        next.splice(index + delta, 0, item);
        onChange(next);
    };

    const update = (id: string, patch: Partial<EmailBlock>) => onChange(blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)));

    return (
        <div className="flex flex-col">
            {blocks.map((block, index) => {
                const selected = selectedId === block.id;
                return (
                    <React.Fragment key={block.id}>
                        {!disabled && index > 0 && <AddBlockMenu onAdd={(kind) => insertAt(index, kind)} />}
                        <div
                            onClick={() => setSelectedId(block.id)}
                            className={`rounded-xl border transition-shadow cursor-pointer ${
                                selected
                                    ? 'border-teal-400 shadow-sm bg-white dark:bg-slate-800'
                                    : 'border-gray-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/60 hover:border-gray-300'
                            }`}
                        >
                            <div className="flex items-center gap-2 px-3 py-2">
                                <span className={selected ? 'text-teal-600 dark:text-teal-300' : 'text-gray-400'}>{BLOCK_ICONS[block.kind]}</span>
                                <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">{EMAIL_BLOCK_LABELS[block.kind]}</span>
                                {!selected && <span className="flex-1 min-w-0 truncate text-xs text-gray-400">{summary(block)}</span>}
                                {selected && <span className="flex-1" />}
                                {!disabled && (
                                    <span className="flex items-center">
                                        <IconButton label="Subir" disabled={index === 0} onClick={() => move(index, -1)}>
                                            <HiOutlineArrowUp size={14} />
                                        </IconButton>
                                        <IconButton label="Descer" disabled={index === blocks.length - 1} onClick={() => move(index, 1)}>
                                            <HiOutlineArrowDown size={14} />
                                        </IconButton>
                                        <IconButton
                                            label="Duplicar bloco"
                                            onClick={() =>
                                                onChange([...blocks.slice(0, index + 1), { ...block, id: newBlockId() }, ...blocks.slice(index + 1)])
                                            }
                                        >
                                            <HiOutlineDuplicate size={14} />
                                        </IconButton>
                                        <IconButton label="Excluir bloco" danger onClick={() => onChange(blocks.filter((b) => b.id !== block.id))}>
                                            <HiOutlineTrash size={14} />
                                        </IconButton>
                                    </span>
                                )}
                            </div>
                            {selected && (
                                <div className="px-3 pb-3 cursor-auto" onClick={(e) => e.stopPropagation()}>
                                    <EmailBlockFields
                                        block={block}
                                        variables={variables}
                                        disabled={disabled}
                                        onChange={(patch) => update(block.id, patch)}
                                    />
                                </div>
                            )}
                        </div>
                    </React.Fragment>
                );
            })}
            {!disabled && (
                <div className="mt-3">
                    <AddBlockMenu variant="footer" onAdd={(kind) => insertAt(blocks.length, kind)} />
                </div>
            )}
        </div>
    );
};

export default EmailBlockList;
